import React from 'react';
import { Card, Divider } from 'antd';
import { Entity } from 'store/organisation/models';
import styled from 'styled-components';

/** Matches Users card field rows */
const renderCardFieldRow = (
  label: React.ReactNode,
  content: React.ReactNode | null | undefined
) => {
  if (content === null || content === undefined) return null;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-muted, #5a6a6a)',
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '18px',
          fontFamily: 'Inter',
        }}
      >
        {label}
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'right',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '20px',
          color: 'var(--color-text-dark, #042a2a)',
          fontFamily: 'Inter',
        }}
      >
        {content}
      </div>
    </div>
  );
};

const CardDetailsPanel = styled.div`
  background: var(--color-surface, #f9fbfc);
  border-radius: 8px;
  border: 1px solid var(--color-border, #ecf2f3);
  padding: 10px 14px;
`;

export const CardValueActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  width: 100%;
`;

/** Inline tax-return tags inside the card details panel */
export const entityCardReturnTagStyle: React.CSSProperties = {
  margin: 0,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  height: 'auto',
  lineHeight: '16px',
  textAlign: 'right',
  maxWidth: '100%',
};

export interface EntityCardProps {
  record: Entity;
  showUsersSection: boolean;
  labels: {
    country: string;
    mappingName: string;
    users: string;
    vat: string;
    capture: string;
  };
  renderIdentity: (record: Entity) => React.ReactNode;
  renderCountry: (record: Entity) => React.ReactNode;
  renderTaxReturn: (content: string, record: Entity) => React.ReactNode | null;
  renderUsers: (record: Entity) => React.ReactNode;
  renderVat: (content: string) => React.ReactNode;
  renderCaptureStats?: (record: Entity) => React.ReactNode;
  renderActions: (record: Entity) => React.ReactNode;
}

export const EntityCard: React.FC<EntityCardProps> = ({
  record,
  showUsersSection,
  labels,
  renderIdentity,
  renderCountry,
  renderTaxReturn,
  renderUsers,
  renderVat,
  renderCaptureStats,
  renderActions,
}) => {
  const taxReturns = renderTaxReturn('', record);
  const showDetailsSection =
    taxReturns != null || showUsersSection;

  return (
    <Card
      style={{
        borderRadius: 10,
        border: '1px solid var(--color-border, #dbeae3)',
        height: '100%',
        background: 'var(--color-surface-raised, #ffffff)',
        boxShadow: '0px 0px 10px 0px #0000000d',
      }}
      styles={{ body: { padding: 24 } }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {renderIdentity(record)}
        </div>
        <div style={{ flexShrink: 0 }}>{renderActions(record)}</div>
      </div>
      <Divider
        style={{
          margin: '20px 0 14px',
          borderColor: 'var(--color-border, #dbeae3)',
        }}
      />
      {renderCaptureStats &&
        renderCardFieldRow(labels.capture, renderCaptureStats(record))}
      {renderCardFieldRow(labels.vat, renderVat(record.vatNumber))}
      {renderCardFieldRow(labels.country, renderCountry(record))}
      {showDetailsSection && (
        <>
          <Divider
            style={{
              margin: '14px 0 12px',
              borderColor: 'var(--color-border, #dbeae3)',
            }}
          />
          <CardDetailsPanel>
            {taxReturns != null &&
              renderCardFieldRow(labels.mappingName, taxReturns)}
            {showUsersSection &&
              renderCardFieldRow(labels.users, renderUsers(record))}
          </CardDetailsPanel>
        </>
      )}
    </Card>
  );
};
